// src/ui/About.jsx — the person behind Allo.
//
// 🟥 NO TITLE, ANYWHERE. Not chief anything, not founder, not owner. Abdullah
//    asked for this explicitly and it is also the better page: a title on a
//    one-person project is a costume, and the work described below is more
//    convincing without one.
//
// 🟥 NO CONTACT DETAILS. The CV this was written from carries a phone number,
//    a personal email and a home city. A public page is scraped within days and
//    a published phone number cannot be unpublished, so none of the three are
//    here. If a way to get in touch is ever wanted, add an address that can be
//    switched off — not the personal one.
//
// 🟢 First person, because it is his site and because it lets the page say
//    "I am still learning" in a way a third-person bio cannot without sounding
//    either falsely modest or like a disclaimer.

import React from 'react';
import { SiteHeader, SiteFooter } from './SiteChrome';
import { ABOUT, APP, navigate } from '../services/siteRoute';
import portrait from '../assets/images/portrait.webp';
import './about.css';

/* Counted from the codebase, same as the numbers on the Home page. */
const BUILT = [
  ['2', 'complete clients, phone and web'],
  ['10', 'languages, translated throughout'],
  ['1,550', 'interface strings'],
  ['14', 'months, so far'],
];

export default function About() {
  const openApp = (e) => { e.preventDefault(); navigate(APP); };

  return (
    <div className="ab">
      <SiteHeader route={ABOUT} />

      <section className="ab-hero">
        <div className="ab-wrap ab-hero-in">
          <img className="ab-portrait" src={portrait} alt="Abdullah Almatbaee"
            width="480" height="480" decoding="async" />
          <div>
            <p className="ab-eyebrow">About</p>
            <h1>Abdullah Almatbaee</h1>
            <p className="ab-lede">
              I have been building Allo since June last year. It started as something I
              wanted to see whether I could do, and it turned into the thing I work on
              every day.
            </p>
          </div>
        </div>
      </section>

      <div className="ab-stats">
        <div className="ab-wrap ab-stats-in">
          {BUILT.map(([n, label]) => (
            <div className="ab-stat" key={label}>
              <b>{n}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <section className="ab-body">
        <div className="ab-wrap ab-prose">

          <h2>How this started</h2>
          <p>
            In June 2025 I started writing a messaging app. I did not have a plan for it and
            I was not particularly qualified to write one. I wanted to find out what was
            actually involved in the things I used every day — how a message really gets from
            one phone to another, what end-to-end encryption means once you have more than one
            device, why a video call freezes and why the app never tells you why.
          </p>
          <p>
            Fourteen months later Allo is a messenger with two complete clients, encrypted
            one-to-one and group messaging, voice and video calls for individuals and for
            groups, a social feed with its own video pipeline and moderation, temporary links
            that let someone talk to me without installing anything, and an interface
            translated into ten languages. I wrote it.
          </p>

          <h2>I am still early in this</h2>
          <p>
            I want to be straightforward about that. I am not a software engineer by training
            and I have not been doing this for years. A great deal of what is in Allo was
            learned while building the part of Allo that needed it, and a great deal of it was
            built twice because the first version was wrong.
          </p>
          <p>
            The parts of this project I am most pleased with are not the clever ones. They are
            the places where the app is honest with you: a call that names which side of the
            connection is failing instead of showing a frozen picture; privacy settings that
            cost you something when you turn them on, rather than pretending they are free;
            a guest link whose limits are stated rather than discovered. Getting those right
            took longer than the features around them and nobody will ever notice them
            working.
          </p>

          <h2>Before this</h2>
          <p>
            My background is not in software. I studied linguistics, and I have spent most of
            my working life in roles about people — training new employees, running a store,
            facilitating group programmes, and collecting research data for a university.
          </p>
          <p>
            The technical work grew out of that last one. Coordinating research participants
            by hand was slow and error-prone, so I built a web system around the Twilio API
            that scheduled bulk SMS, let participants book themselves in through a single
            link, and generated secure trip links that texted people automatically when their
            ride arrived. That was the first time I built something real because something
            real needed doing, and it is why I started Allo.
          </p>

          <h2>Alongside</h2>
          <div className="ab-facts">
            <div>
              <b>Studying</b>
              <p>
                B.S. in Information Technology at Arizona State University, expected 2028.
                Earlier, a B.A. in Linguistics from the University of Kufa in Iraq.
              </p>
            </div>
            <div>
              <b>Languages</b>
              <p>Arabic, natively. English, fluently. Which is part of why Allo ships in ten.</p>
            </div>
            <div>
              <b>Also worked with</b>
              <p>Python, Linux, PowerShell, REST APIs, and the Twilio platform.</p>
            </div>
            <div>
              <b>Certified in</b>
              <p>Google IT Support Fundamentals, and Diversity Awareness.</p>
            </div>
          </div>

          <h2>Where it goes</h2>
          <p>
            Allo is not finished and I am not treating it as finished. There is a list of
            things that are half-right and a longer list of things I have not attempted yet.
            I would rather ship something I can explain honestly than something that sounds
            more impressive than it is.
          </p>

          <p className="ab-sign">— Abdullah</p>

          <div className="ab-cta">
            <a className="ab-btn" href="/app" onClick={openApp}>Open Allo Web</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
